import { useCallback, useEffect, useRef, useState } from 'react';
import { PhoneSheet } from '../ui/phoneSheet';
import { usePhone } from '../ui/usePhone';
import { useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { AccessScreen } from '@flobi/asset-library/share';
import '@flobi/asset-library/share.css';
import { useTheme } from '../theme';
import { linkComment, linkComments, viewLink, type LinkView } from './artworkShare';
import type { ShareCallError } from './gateway';
import { setTokenGetter } from '../services/apiClient';
import { redirectToSignIn } from '../auth/redirect';
import type { CommentAnchor, CommentThread } from '../services/comments';
import { CommentPin, CommentsPanel } from '../editor/comments/CommentsPanel';
import { Icon } from '../editor/chrome/icons';

/**
 * A file's "Anyone with the link" page (decision 3): its pages as the renderer draws them,
 * for someone with no account. Never the editable document. The token in the address is
 * the credential; a password, when the link has one, is asked for here.
 *
 * The pictures are drawn on the server the first time (and again after the file
 * changes), so the page says so and asks again until they are there.
 */

type State =
  | { status: 'loading' }
  | { status: 'password'; message: string | null }
  | { status: 'gone' }
  | { status: 'error'; message: string }
  | { status: 'ready'; view: LinkView };

const PASSWORD = new Set(['PASSWORD_REQUIRED', 'PASSWORD_INCORRECT', 'PASSWORD_LOCKED']);
const GONE = new Set(['NOT_FOUND', 'REVOKED', 'EXPIRED']);
const POLL_MS = 2500;

export function ViewPage() {
  const { token = '' } = useParams<{ token: string }>();
  const { theme } = useTheme();
  const phone = usePhone();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [password, setPassword] = useState('');
  const used = useRef<string | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [fit, setFit] = useState(true);
  // Someone signed in (any Flobi account) posts as themselves; the rest are asked to sign in.
  const { isSignedIn, getToken } = useAuth();
  if (isSignedIn) setTokenGetter(() => getToken());

  const load = useCallback(
    async (pw?: string) => {
      try {
        const view = await viewLink(token, pw);
        setState({ status: 'ready', view });
      } catch (err) {
        const e = err as ShareCallError;
        if (e.code && PASSWORD.has(e.code)) setState({ status: 'password', message: e.code === 'PASSWORD_REQUIRED' ? null : (e.userMessage ?? null) });
        else if ((e.code && GONE.has(e.code)) || e.status === 404 || e.status === 410) setState({ status: 'gone' });
        else setState({ status: 'error', message: e.userMessage ?? "Couldn't open this file. Check your connection and try again." });
      }
    },
    [token],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Still being drawn (or a newer drawing coming): ask again shortly.
  const drawing = state.status === 'ready' && (state.view.pictures.status === 'drawing' || (state.view.pictures.status === 'ready' && state.view.pictures.newerComing));
  useEffect(() => {
    if (!drawing) return undefined;
    const timer = setTimeout(() => void load(used.current), POLL_MS);
    return () => clearTimeout(timer);
  }, [drawing, load, state]);

  /* ── comments, on a Can-comment link (W5-AR2) ─────────────────────────── */
  const commentable = state.status === 'ready' && state.view.role === 'comment';
  const [threads, setThreads] = useState<CommentThread[] | null>(null);
  const [commentsFailed, setCommentsFailed] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [commenting, setCommenting] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [draft, setDraft] = useState<CommentAnchor | null>(null);
  const readComments = useCallback(() => {
    linkComments(token, used.current)
      .then((answer) => {
        setThreads(answer.threads);
        setCommentsFailed(false);
      })
      .catch(() => setCommentsFailed(true));
  }, [token]);
  useEffect(() => {
    if (commentable) readComments();
  }, [commentable, readComments, isSignedIn]);
  const post = (body: string) => {
    const anchor = draft;
    if (!anchor) return;
    if (!isSignedIn) {
      // Posting takes an account: a visitor who is signed out is sent to sign in, and back here.
      redirectToSignIn();
      return;
    }
    setDraft(null);
    setCommenting(false);
    linkComment(token, used.current, anchor, body)
      .then((made) => {
        setThreads((now) => [...(now ?? []), made]);
        setChosen(made.id);
      })
      .catch((err: ShareCallError) => {
        if (err.code === 'SIGN_IN' || err.status === 401) redirectToSignIn();
        else setCommentsFailed(true);
      });
  };

  if (state.status === 'gone') {
    return (
      <div className="min-h-dvh bg-page">
        <AccessScreen state="link-off" thing={{ type: 'file', id: '', name: 'this file', noun: 'file' }} theme="artwork" mode={theme === 'light' ? 'light' : 'dark'} />
      </div>
    );
  }

  if (state.status === 'loading') return <Centered>Opening…</Centered>;

  if (state.status === 'error') {
    return (
      <Centered>
        <p>{state.message}</p>
        <button type="button" className="text-accent-text underline" onClick={() => void load(used.current)}>
          Try again
        </button>
      </Centered>
    );
  }

  if (state.status === 'password') {
    return (
      <Centered>
        <form
          className="flex w-[min(320px,100%)] flex-col gap-[10px] text-left"
          onSubmit={(event) => {
            event.preventDefault();
            if (!password) return;
            used.current = password;
            void load(password);
          }}
        >
          <h1 className="text-[17px] font-semibold text-ink">This file needs a password</h1>
          <p className="text-[13px] text-ink-muted">Whoever sent you the link has it.</p>
          <input
            type="password"
            autoFocus
            aria-label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="h-[36px] rounded-[10px] bg-fill-1 px-[12px] text-[13px] text-ink outline-none"
          />
          {state.message ? (
            <p role="alert" className="text-[12px] text-danger">
              {state.message}
            </p>
          ) : null}
          <button type="submit" disabled={!password} className="h-[34px] rounded-full bg-accent text-[13px] font-medium text-on-accent disabled:opacity-40">
            Open
          </button>
        </form>
      </Centered>
    );
  }

  const { view } = state;
  const pictures = view.pictures;
  const pages = pictures.status === 'ready' ? pictures.pages : [];
  const current = pages[Math.min(page, Math.max(0, pages.length - 1))];

  return (
    <div className="flex h-dvh flex-col bg-page text-ink">
      <header className="flex h-[52px] shrink-0 items-center gap-[10px] px-[16px] max-md:h-auto max-md:flex-wrap max-md:gap-y-[6px] max-md:py-[8px]">
        <h1 className="min-w-0 flex-1 truncate text-[14px] font-semibold max-md:basis-full">{view.name}</h1>
        {pages.length > 1 ? (
          <span className="flex items-center gap-[4px] text-[13px] text-ink-muted">
            <button type="button" aria-label="Previous board" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="size-[28px] rounded-full hover:bg-fill-2 disabled:opacity-40 pointer-coarse:size-[44px]">
              ‹
            </button>
            <span className="tabular-nums">{`${Math.min(page, pages.length - 1) + 1} of ${pages.length}`}</span>
            <button type="button" aria-label="Next board" disabled={page >= pages.length - 1} onClick={() => setPage((p) => Math.min(pages.length - 1, p + 1))} className="size-[28px] rounded-full hover:bg-fill-2 disabled:opacity-40 pointer-coarse:size-[44px]">
              ›
            </button>
          </span>
        ) : null}
        {current ? (
          <button type="button" onClick={() => setFit((f) => !f)} className="h-[28px] rounded-full bg-fill-1 px-[12px] text-[12px] hover:bg-fill-selected pointer-coarse:h-[44px]">
            {fit ? 'Actual size' : 'Fit'}
          </button>
        ) : null}
        {commentable ? (
          <button type="button" aria-pressed={commentsOpen} onClick={() => setCommentsOpen((open) => !open)} className={`flex h-[28px] items-center gap-[6px] rounded-full px-[12px] text-[12px] pointer-coarse:h-[44px] ${commentsOpen ? 'bg-fill-selected' : 'bg-fill-1 hover:bg-fill-selected'}`}>
            <Icon name="toolbar-comments" size={14} />
            Comments
          </button>
        ) : null}
        {view.allowDownload && current?.url ? (
          <a href={current.url} download={current.name} className="h-[28px] rounded-full bg-fill-1 px-[12px] text-[12px] leading-[28px] hover:bg-fill-selected pointer-coarse:h-[44px] pointer-coarse:leading-[44px]">
            Download
          </a>
        ) : null}
      </header>
      <div className="flex min-h-0 flex-1">
      <main className={`relative flex min-h-0 flex-1 justify-center ${fit ? 'items-center overflow-hidden' : 'items-start overflow-auto'} p-[16px]`}>
        {pictures.status === 'failed' ? (
          <p className="self-center text-[13px] text-ink-muted">{pictures.message}</p>
        ) : pictures.status === 'drawing' ? (
          <p className="self-center text-[13px] text-ink-muted" role="status">
            Drawing the file…
          </p>
        ) : current?.url ? (
          <span className={`relative inline-block ${fit ? 'max-h-full max-w-full' : ''} ${commenting ? 'cursor-crosshair' : ''}`}>
            <img
              src={current.url}
              alt={current.name}
              draggable={view.allowDownload && !commenting}
              onContextMenu={view.allowDownload ? undefined : (event) => event.preventDefault()}
              onClick={(event) => {
                if (!commenting || !current.artboardId) return;
                const box = event.currentTarget.getBoundingClientRect();
                const boardX = ((event.clientX - box.left) / box.width) * current.width;
                const boardY = ((event.clientY - box.top) / box.height) * current.height;
                setDraft({ pageId: current.pageId ?? '', nodeId: null, x: boardX, y: boardY, boardId: current.artboardId, boardX, boardY });
                setChosen(null);
                setCommentsOpen(true);
              }}
              className={`block ${fit ? 'max-h-[calc(100dvh-84px)] max-w-full object-contain' : 'max-w-none'}`}
            />
            {commentable && current.artboardId
              ? [...(threads ?? []).filter((one) => one.anchor.boardId === current.artboardId), ...(draft?.boardId === current.artboardId ? [null] : [])].map((thread, index) => {
                  const anchor = thread ? thread.anchor : draft!;
                  const left = `${((anchor.boardX ?? 0) / current.width) * 100}%`;
                  const top = `${((anchor.boardY ?? 0) / current.height) * 100}%`;
                  return (
                    <span key={thread?.id ?? 'draft'} className="absolute size-0" style={{ left, top }}>
                      <CommentPin x={0} y={0} label={String(index + 1)} chosen={thread ? thread.id === chosen : true} draft={!thread} onPress={thread ? () => setChosen(thread.id === chosen ? null : thread.id) : undefined} />
                    </span>
                  );
                })
              : null}
          </span>
        ) : (
          <p className="self-center text-[13px] text-ink-muted">This board can’t be shown right now. Try again in a minute.</p>
        )}
        {commentable && current?.artboardId ? (
          <div role="toolbar" aria-label="Tools" className="glass-thick absolute bottom-[16px] left-1/2 flex -translate-x-1/2 rounded-full p-[4px]">
            <button type="button" aria-label="Comment" aria-pressed={commenting} title="Comment" onClick={() => setCommenting((on) => !on)} className={`grid size-[32px] place-items-center rounded-full pointer-coarse:size-[44px] ${commenting ? 'bg-accent text-on-accent' : 'text-label hover:bg-fill-1'}`}>
              <Icon name="toolbar-comments" size={18} />
            </button>
          </div>
        ) : null}
      </main>
      {commentable && commentsOpen ? sheetOnPhone(phone, () => setCommentsOpen(false),
        <CommentsPanel
          className="m-[8px] mt-0 w-[300px] shrink-0 rounded-[16px]"
          threads={threads}
          failed={commentsFailed}
          canComment={false}
          chosen={chosen}
          numbers={new Map((threads ?? []).filter((one) => one.anchor.boardId === current?.artboardId).map((one, index) => [one.id, index + 1] as const))}
          draft={draft !== null}
          people={[]}
          onChoose={setChosen}
          onPostDraft={(body) => post(body)}
          onCancelDraft={() => setDraft(null)}
          onReply={() => undefined}
          onRetry={readComments}
          onClose={() => setCommentsOpen(false)}
        />,
      ) : null}
      </div>
    </div>
  );
}

/** The comments beside the board on a laptop; on a phone, a sheet over it (the responsive pass, 2026-10-26). */
function sheetOnPhone(phone: boolean, onClose: () => void, panel: React.ReactNode) {
  return phone ? <PhoneSheet label="Comments" onClose={onClose}>{panel}</PhoneSheet> : panel;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-dvh flex-col items-center justify-center gap-[12px] bg-page p-[16px] text-center text-[13px] text-ink-muted">{children}</div>;
}
